import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { IconComponent } from './actions';
import { cx } from './cx';

export interface TabItem<T extends string = string> {
  id: T;
  label: React.ReactNode;
  icon?: IconComponent;
  /** Aba como rota (ex.: /admin/crm/vendas) em vez de estado local. */
  to?: string;
  badge?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange?: (id: T) => void;
  /**
   * underline: navegação entre seções da página (rola na horizontal no mobile).
   * segmented: alternância de 2–4 opções (idioma, Imagem/Vídeo, Links/Reservas).
   */
  variant?: 'underline' | 'segmented';
  ariaLabel?: string;
  className?: string;
}

/** Abas padrão do sistema — use no lugar de botões com border-b-2/bg-primary manuais. */
function Tabs<T extends string>({
  items,
  value,
  onChange,
  variant = 'underline',
  ariaLabel,
  className,
}: TabsProps<T>) {
  const refs = useRef<Array<HTMLElement | null>>([]);
  const underline = variant === 'underline';

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    let next = index;
    for (let i = 0; i < items.length; i++) {
      next = (next + step + items.length) % items.length;
      if (!items[next].disabled) break;
    }
    refs.current[next]?.focus();
    if (!items[next].to) onChange?.(items[next].id);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(underline ? 'tabs' : 'segmented', className)}
    >
      {items.map((item, index) => {
        const active = item.id === value;
        const Icon = item.icon;
        const classes = underline
          ? cx('tabs__tab', active && 'tabs__tab--active', item.disabled && 'opacity-50 pointer-events-none')
          : cx('segmented__btn', active && 'segmented__btn--active', item.disabled && 'opacity-50');
        const content = (
          <>
            {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
            <span>{item.label}</span>
            {item.badge}
          </>
        );
        const a11y = {
          role: 'tab',
          'aria-selected': active,
          tabIndex: active ? 0 : -1,
          onKeyDown: (e: React.KeyboardEvent) => onKeyDown(e, index),
        };

        if (item.to) {
          return (
            <Link
              key={item.id}
              to={item.to}
              ref={(el) => {
                refs.current[index] = el;
              }}
              className={classes}
              onClick={() => onChange?.(item.id)}
              {...a11y}
            >
              {content}
            </Link>
          );
        }

        return (
          <button
            key={item.id}
            type="button"
            ref={(el) => {
              refs.current[index] = el;
            }}
            className={classes}
            disabled={item.disabled}
            onClick={() => onChange?.(item.id)}
            {...a11y}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
